import type { AgentApprovalMode, AgentToolPolicy } from '@this+that/models';
import { StateCreator } from 'zustand';

import {
  handleAddConversationAssistantMessage,
  handleAddConversationUserMessage,
  handleApplyCreatedDraft,
  handleApplyTemplateSnapshot,
  handleClearConversationTranscript,
  handleClearSuggestedDestinations,
  handleCompleteWithTemplate,
  handleEnterAgentSetup,
  handleExitAgentSetup,
  handleGoToPhase,
  handleReopenFinishedSetup,
  handleSetApprovalMode,
  handleSetConversationThinking,
  handleSetDoneSummary,
  handleSetDraftCaveats,
  handleSetDraftPatch,
  handleSetDraftRegenerating,
  handleSetDraftResultDelivery,
  handleSetDraftSaving,
  handleSetDraftSuggestions,
  handleSetOutbound,
  handleSetToolPolicies,
  handleSetToolSets,
  handleSetVisibility,
} from './handlers';
import {
  AgentSetupAssistantTurnMessage,
  AgentSetupDraftState,
  AgentSetupPanelState,
  AgentSetupPanelStore,
  AgentSetupTrialDoneSummary,
  CreatedAgentDraft,
  EnterAgentSetupParams,
  HandlerSet,
} from './types';
import type { AgentOutboundDraft } from '../../components/AgentOutboundSettings/agentOutboundDraft';
import { AgentSetupPanelPhase } from '../../enums';
import {
  AgentSetupResultDelivery,
  AgentTemplateVisibility,
} from '../../services/agentTemplates.service';

export const INITIAL_AGENT_SETUP_PANEL_STATE: AgentSetupPanelState = {
  phaseByItemId: {},
  draftByItemId: {},
  conversationByItemId: {},
  finishedByItemId: {},
};

export const createAgentSetupPanelSlice: StateCreator<AgentSetupPanelStore> = (
  set,
) => {
  const handlerSet: HandlerSet = (updater) => {
    if (typeof updater === 'function') {
      set((fullState) => updater(fullState) as Partial<AgentSetupPanelStore>);
    } else {
      set(updater as Partial<AgentSetupPanelStore>);
    }
  };

  return {
    ...INITIAL_AGENT_SETUP_PANEL_STATE,

    enterAgentSetup: (params: EnterAgentSetupParams) =>
      handleEnterAgentSetup(handlerSet, params),
    exitAgentSetup: (itemId: string) =>
      handleExitAgentSetup(handlerSet, itemId),
    reopenFinishedSetup: (itemId: string) =>
      handleReopenFinishedSetup(handlerSet, itemId),
    goToPhase: (itemId: string, phase: AgentSetupPanelPhase) =>
      handleGoToPhase(handlerSet, itemId, phase),
    addConversationUserMessage: (itemId: string, content: string) =>
      handleAddConversationUserMessage(handlerSet, itemId, content),
    addConversationAssistantMessage: (
      itemId: string,
      message: AgentSetupAssistantTurnMessage,
    ) => handleAddConversationAssistantMessage(handlerSet, itemId, message),
    setConversationThinking: (itemId: string, isThinking: boolean) =>
      handleSetConversationThinking(handlerSet, itemId, isThinking),
    clearConversationTranscript: (itemId: string) =>
      handleClearConversationTranscript(handlerSet, itemId),
    setDraftCaveats: (itemId: string, caveats: string[]) =>
      handleSetDraftCaveats(handlerSet, itemId, caveats),
    clearSuggestedDestinations: (itemId: string) =>
      handleClearSuggestedDestinations(handlerSet, itemId),
    setDraftSuggestions: (
      itemId: string,
      suggestions: Pick<
        AgentSetupDraftState,
        'missingBrainReferences' | 'suggestedDestinations'
      >,
    ) => handleSetDraftSuggestions(handlerSet, itemId, suggestions),
    setDraftResultDelivery: (
      itemId: string,
      resultDelivery: AgentSetupResultDelivery,
    ) => handleSetDraftResultDelivery(handlerSet, itemId, resultDelivery),
    applyCreatedDraft: (itemId: string, draft: CreatedAgentDraft) =>
      handleApplyCreatedDraft(handlerSet, itemId, draft),
    applyTemplateSnapshot: (itemId: string, template: CreatedAgentDraft) =>
      handleApplyTemplateSnapshot(handlerSet, itemId, template),
    completeWithTemplate: (
      itemId: string,
      template: { templateId: string; name: string },
    ) => handleCompleteWithTemplate(handlerSet, itemId, template),
    setDraftPatch: (
      itemId: string,
      patch: Partial<Pick<AgentSetupDraftState, 'name' | 'job'>>,
    ) => handleSetDraftPatch(handlerSet, itemId, patch),
    setToolPolicies: (itemId: string, toolPolicies: AgentToolPolicy[]) =>
      handleSetToolPolicies(handlerSet, itemId, toolPolicies),
    setToolSets: (itemId: string, toolSets: string[]) =>
      handleSetToolSets(handlerSet, itemId, toolSets),
    setVisibility: (itemId: string, visibility: AgentTemplateVisibility) =>
      handleSetVisibility(handlerSet, itemId, visibility),
    setApprovalMode: (itemId: string, approvalMode: AgentApprovalMode) =>
      handleSetApprovalMode(handlerSet, itemId, approvalMode),
    setOutbound: (itemId: string, outbound: AgentOutboundDraft) =>
      handleSetOutbound(handlerSet, itemId, outbound),
    setDraftSaving: (itemId: string, isSaving: boolean) =>
      handleSetDraftSaving(handlerSet, itemId, isSaving),
    setDraftRegenerating: (itemId: string, isRegenerating: boolean) =>
      handleSetDraftRegenerating(handlerSet, itemId, isRegenerating),
    setDoneSummary: (itemId: string, summary: AgentSetupTrialDoneSummary) =>
      handleSetDoneSummary(handlerSet, itemId, summary),
    reset: () => set(INITIAL_AGENT_SETUP_PANEL_STATE),
  };
};
